import { Box, Typography, Button, InputAdornment, OutlinedInput, Stack, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Tooltip, IconButton } from "@mui/material";
import { useCallback, useContext, useRef, useState } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import DeleteIcon from '@mui/icons-material/Delete';
import theme from "../../../theme";
import ConfirmDialog from "../ConfirmDialog";
import { GroupSettingsContext } from "../../../contexts/GroupSettingsContext";
import StatusBadge from "../../molecules/status/StatusBadge";
import { DragDropContext, Draggable, Droppable, DropResult } from "react-beautiful-dnd";
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import React from "react";
import { scrollbarStyle } from "../../../shared/dashboard";
import { DashboardsContext } from "../../../contexts/DashboardsContext";
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ClearIcon from '@mui/icons-material/Clear';
import { updateDashboardFunc } from "../../../lib/helper";
import StarIcon from '@mui/icons-material/Star';
import StarOutlineIcon from '@mui/icons-material/StarOutline';
import { ReactComponent as Status } from "../../../assets/icons/status.svg";

const useStyles = makeStyles((theme) => ({
    dialog: {
        "& .MuiDialog-paper": {
            width: 500,
            height: 'fit-content',
            minHeight: '30vh',
            maxHeight: '70vh',
            padding: '16px 24px',
            borderRadius: 24,
        }
    },
    dialogTitle: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        padding: 'unset',
        color: 'black',
        fontWeight: 'bold',
        fontFamily: 'Inter',
        fontSize: '1.3rem',
    },
    dialogContent: {
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        padding: '8px 0',
        height: '100%',
        overflowY: 'hidden',
    },
    dialogActions: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'flex-end',
        paddingTop: '16px',
    },
    headerIcon: {
        stroke: theme.colors.neutral['500'],
        width: 32,
        height: 32,
    },
    list: {
        overflowY: 'auto',
        ...scrollbarStyle,
    },
    input: {
        height: 'fit-content',
        borderRadius: 8,
        border: `1px solid ${theme.colors.neutral['400']}`,
        '&::placeholder': {
            fontSize: '14px',
            lineHeight: '21.7px',
            fontWeight: 400,
            color: theme.colors.neutral['600']
        }
    },
    icon: {
        padding: 0,
        color: theme.colors.neutral['500'],
        transition: 'ease-in-out 300ms',
        '&:hover': {
            color: theme.colors.neutral['700'],
        }
    },
    cancelBtn: {
        height: '40px',
        width: '88px',
        padding: '8px 24px',
        borderRadius: '40px',
        backgroundColor: theme.palette.primary.main,
        color: 'white',
        fontWeight: 'bold',
        textTransform: 'none',
        transition: 'ease-in-out 300ms',
    },
    addStatus: {
        fontFamily: 'Inter',
        fontSize: '1rem',
        fontWeight: 600,
        cursor: 'pointer',
        userSelect: 'none',
        color: theme.colors.primary['500'],
    },
    saveStatus: {
        fontFamily: 'Inter',
        fontSize: '0.9rem',
        fontWeight: 600,
        color: theme.colors.primary['500'],
        textTransform: 'none',
        minWidth: '0 !important',
        padding: '0 !important',
        '&:hover': {
            background: '#fff !important',
        }
    }
}));

const ManageStatusModal: React.FC<{
    open: boolean,
    onClose: () => void,
}> = ({ open, onClose }) => {
    const classes = useStyles();
    const { defaultStatus, statuses, setDefaultStatus, setStatuses } = useContext(GroupSettingsContext);
    const { dashboards, setDashboards } = useContext(DashboardsContext);
    const [bucket, setBucket] = useState<string>('');
    const [editStatus, setEditStatus] = useState<string|null>(null);
    const [newStatus, setNewStatus] = useState<string>('');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [deletableStatus, setDeletableStatus] = useState('');
    const statusesRef = useRef<string[]>(structuredClone(statuses));

    const dragAndDropStatus = useCallback((result: DropResult) => {
        const { source, destination, type } = result;

        if (!destination)
            return;

        switch (type) {
            case 'group': (async () => {
                const sourceIdx = source.droppableId === 'dnd-statuses' ? source.index : -1;
                const destIdx = destination.droppableId === 'dnd-statuses' ? destination.index : -1;

                if (sourceIdx !== destIdx && destIdx >= 0) {
                    const updatedStatus = [...statuses];
                    const movableStatus = updatedStatus.splice(sourceIdx, 1)[0];

                    updatedStatus.splice(destIdx, 0, movableStatus);
                    setStatuses(updatedStatus);
                    statusesRef.current = updatedStatus;
                }
            })(); break;
        default:
            break;
        }
    }, [statuses, setStatuses]);

    const isExistingBucket = useCallback((edited: string) =>
        !!statuses.find(status => status.trim().toLowerCase() === edited.trim().toLowerCase())
    , [statuses]);

    const isRemovableBucket = useCallback((selected: string) =>
        !dashboards.find(dashboard => dashboard.status.toLowerCase() === selected.toLowerCase())
    , [dashboards]);

    const handleCreateBucket = useCallback(() => {
        setStatuses(prev => [...prev, bucket]);
        statusesRef.current = [...statusesRef.current, bucket];
        setBucket('');
    }, [bucket, setStatuses]);

    const handleEditBucket = useCallback(() => {
        const updatedDashboards = [...dashboards];
        const filteredDashboards = updatedDashboards.filter(({status}) => status.trim().toLowerCase() === editStatus?.trim().toLowerCase());
        const promises: Promise<boolean>[] = [];

        filteredDashboards.forEach(dashboard => promises.push(updateDashboardFunc({...dashboard, status: newStatus})));
        Promise.all(promises).then(() => {
            updatedDashboards.forEach(dashboard => {
                if (dashboard.status.trim().toLowerCase() === editStatus?.trim().toLowerCase())
                    dashboard.status = newStatus;
            });

            statusesRef.current = statusesRef.current.map(status => status === editStatus ? newStatus : status);
            setStatuses(statusesRef.current);
            setDashboards(updatedDashboards);
            setEditStatus(null);
            setNewStatus('');
        });
    }, [dashboards, editStatus, newStatus, setDashboards, setStatuses]);

    const handleRemoveBucket = useCallback((selected: string) => {
        setStatuses(prev => prev.filter(status => status.toLowerCase() !== selected.toLowerCase()));
        statusesRef.current = statusesRef.current.filter(status => status.toLowerCase() !== selected.toLowerCase());
        setBucket('');
    }, [setStatuses]);

    return (<>
        <Dialog className={classes.dialog} open={open} onClose={() => onClose()}>
            <DialogTitle className={classes.dialogTitle}>
                <Stack direction="row" spacing={2} alignItems="center" justifyContent="flex-start">
                    <Status className={classes.headerIcon} />
                    <span>{'Manage statuses'}</span>
                </Stack>
            </DialogTitle>
            <Divider sx={{ margin: '8px 0'}} />
            <DialogContent className={classes.dialogContent}>
                <Typography fontFamily="Inter" fontSize="1rem" color={theme.colors.neutral['600']}>
                    {'Add, move, or delete buckets: '}
                </Typography>
                <OutlinedInput type="text"
                    className={classes.input}
                    placeholder="Add new bucket"
                    onChange={(e) => setBucket(e.target.value)}
                    fullWidth
                    endAdornment={!!bucket.trim() && (
                        <InputAdornment position="end" onClick={handleCreateBucket}>
                            <Typography className={classes.addStatus}>Add</Typography>
                        </InputAdornment>
                    )} />
                <DragDropContext onDragEnd={dragAndDropStatus}>
                    <Droppable droppableId="dnd-statuses" type="group" direction="vertical" isDropDisabled={(editStatus !== null)}>
                        {(provided) => (
                            <Stack direction="column" className={classes.list} alignItems="flex-start"
                                {...provided.droppableProps} ref={provided.innerRef}>
                                {statusesRef.current.map((status, i) => (
                                    <Draggable draggableId={'dnd-bucket:' + status} index={i} key={'modify-bucket-modal-185-' + status} isDragDisabled={(editStatus !== null)}>
                                        {(provided) => (
                                            <Stack direction="row" alignItems="center" justifyContent="space-between"
                                                {...provided.draggableProps} ref={provided.innerRef} width="100%"
                                                style={{ ...provided.draggableProps.style, marginTop: 16, }}>
                                                <Stack direction="row" spacing={2} alignItems="center" width="85%">
                                                    <Box {...provided.dragHandleProps}>
                                                        <DragIndicatorIcon fontSize="medium" sx={{ "&.MuiSvgIcon-root": {fill: '#666666'} }} />
                                                    </Box>
                                                    {(editStatus === status) ? (
                                                        <OutlinedInput type="text"
                                                            className={classes.input}
                                                            placeholder="Add new bucket"
                                                            defaultValue={editStatus}
                                                            onChange={(e) => setNewStatus(e.target.value)}
                                                            fullWidth
                                                            endAdornment={
                                                                <InputAdornment position="end">
                                                                    <Button variant="text"
                                                                        className={classes.saveStatus}
                                                                        onClick={handleEditBucket}
                                                                        disabled={!newStatus.trim() || isExistingBucket(newStatus)}
                                                                        disableRipple> {'Save'} </Button>
                                                                </InputAdornment>
                                                            } />
                                                    ) : (<StatusBadge title={status} />)}
                                                </Stack>
                                                <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
                                                    <Tooltip placement="left"
                                                        title={<Typography fontSize="0.85rem" fontWeight="bold">
                                                            {'Set as default for new companies'}
                                                        </Typography>}>
                                                        <IconButton size="small"
                                                            className={classes.icon}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setDefaultStatus(status);
                                                            }} disableRipple>
                                                            {defaultStatus === status ? (
                                                                <StarIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />
                                                            ) : (
                                                                <StarOutlineIcon fontSize="small"  />
                                                            )}
                                                        </IconButton>
                                                    </Tooltip>
                                                    {(editStatus !== status) ? (
                                                        <IconButton size="small"
                                                            className={classes.icon}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setEditStatus(status);
                                                                setNewStatus(status);
                                                            }} disableRipple>
                                                            <EditOutlinedIcon fontSize="small" />
                                                        </IconButton>
                                                    ) : (
                                                        <IconButton size="small"
                                                            className={classes.icon}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setEditStatus(null);
                                                                setNewStatus('');
                                                            }} disableRipple>
                                                            <ClearIcon fontSize="small" />
                                                        </IconButton>
                                                    )}
                                                    {isRemovableBucket(status) ? (
                                                        <IconButton size="small"
                                                            className={classes.icon}
                                                            onClick={() => {
                                                                setDeletableStatus(status);
                                                                setDialogOpen(true);
                                                            }} disabled={!!editStatus}
                                                            disableRipple>
                                                            <DeleteIcon sx={!!editStatus ? { "&.MuiSvgIcon-root": {fill: 'lightgray'} } : {}} />
                                                        </IconButton>
                                                    ) : (
                                                        <Tooltip title="Please move all associated dashboards to other statuses before deleting this status.">
                                                            <DeleteIcon sx={{ "&.MuiSvgIcon-root": {fill: 'lightgray'} }} />
                                                        </Tooltip>
                                                    )}
                                                </Stack>
                                            </Stack>
                                        )}
                                    </Draggable>
                                ))}
                                {provided.placeholder}
                            </Stack>
                        )}
                    </Droppable>
                </DragDropContext>
            </DialogContent>
            <DialogActions className={classes.dialogActions}>
                <Button variant="contained" className={classes.cancelBtn} onClick={onClose}> {'Close'} </Button>
            </DialogActions>
        </Dialog>
        <ConfirmDialog open={dialogOpen}
            title="Delete confirmation"
            content={`Are you sure you want to delete this bucket?`}
            confirmCallback={() => {
                handleRemoveBucket(deletableStatus);
                setDeletableStatus('');
                setDialogOpen(false);
            }}
            cancelCallback={() => setDialogOpen(false)}
        />
    </>);
}

export default ManageStatusModal;
